import * as React from 'react';
import { useEffect, useState } from 'react';
import { Theme, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { Bar, ChartData } from 'react-chartjs-2';
import * as chartjs from 'chart.js';
import { salesChartMode } from 'common/types';
import Spinner from './Spinner';
import moment from 'moment-timezone';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	data: null | ChartData<chartjs.ChartData>;
	dataStatus: {
		loading: boolean;
		error?: string;
	};
	year: null | number;
	month: null | string;
	day: null | moment.Moment;
	viewMode: salesChartMode;
}

type Props = OwnProps & WithStyles<typeof styles>;

const DashboardChart = (props: Props) => {
	const { classes, data, dataStatus } = props;
	const [options, setOptions] = useState({});
	const [plugins, setPlugins] = useState<object[]>([]);

	const barChartRef = React.createRef<Bar>();
	const { t } = useTranslation();

	useEffect(() => {
		const optionsObject = {
			legend: {
				display: true,
				labels: {
					fontColor: classes.basicText,
					usePointStyle: true,
				},
				position: 'bottom',
			},
			scales: {
				xAxes: [
					{
						stacked: true,
						gridLines: {
							display: false,
						},
					},
				],
				yAxes: [
					{
						stacked: true,
						ticks: {
							beginAtZero: true,
						},
						gridLines: {
							display: true,
						},
					},
				],
			},
		};
		setOptions(optionsObject);
		const plugins = [
			{
				afterDatasetDraw: weekSeparatorPlugin,
			},
		];
		setPlugins(plugins);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const weekSeparatorPlugin = (chart: Chart) => {
		if (props.viewMode === 'month') {
			const meta = chart.getDatasetMeta(0);
			const ruler = meta.controller._ruler;
			const sunday = moment().isoWeekday(7).format('ddd');
			const barWidth = ruler.scale.width / (ruler.scale.maxIndex + 1);
			const sundayIndices: number[] = ruler.scale.ticks.reduce(
				(a: number[], e: string, i: number) => {
					if (e.startsWith(sunday)) {
						a.push(i);
					}
					return a;
				},
				[],
			);
			const context = chart.ctx!;
			context.save();
			for (const index of sundayIndices) {
				const lineLeftOffset = ruler.scale.left + barWidth * (index + 1);
				context.beginPath();
				context.strokeStyle = 'rgba(0, 0, 0, 0.1)';
				context.moveTo(lineLeftOffset, 0);
				context.lineTo(lineLeftOffset, ruler.scale.top);
				context.stroke();
			}
			context.restore();
		}
	};

	if (dataStatus.loading) {
		return (
			<div className={classes.chartContainer}>
				<div className={classes.spinnerContainer}>
					<Spinner />
				</div>
				<Typography variant="body1" align="center" gutterBottom>
					{t('reports.sales.loadingSalesData', 'Loading sales data') + '...'}
				</Typography>
			</div>
		);
	}

	if (dataStatus.error) {
		return (
			<div className={classes.chartContainer}>
				<Typography variant="body1" align="center">
					{dataStatus.error}
				</Typography>
			</div>
		);
	}

	if (!data) {
		return null;
	}

	return (
		<div className={classes.chartContainer}>
			<Bar ref={barChartRef} data={data} options={options} plugins={plugins} height={90} />
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		spinnerContainer: {
			width: '100%',
			height: 100,
			position: 'relative',
		},
		chartContainer: {
			width: '100%',
		},
		basicText: {
			color: theme.palette.text.primary,
		},
	});

export default withStyles(styles)(DashboardChart);
